import type { ReactNode } from "react";

import { RelativeTime } from "@/components/relative-time";
import i18n from "@/i18n";
import { cn } from "@/lib/utils";

const DAY_MS = 24 * 60 * 60 * 1000;

// Static map — Tailwind can't see dynamically-built class names.
const COLS: Record<3 | 4 | 5 | 6 | 7, string> = {
  3: "grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-2 lg:grid-cols-4",
  5: "grid-cols-2 lg:grid-cols-5",
  6: "grid-cols-2 lg:grid-cols-6",
  7: "grid-cols-2 lg:grid-cols-7",
};

/**
 * At-a-glance status row: one bordered block with hairline-divided cells so
 * it reads as a single panel, not a grid of competing cards. Each cell's
 * `border-l border-t` pairs with the grid's -ml-px/-mt-px to form the
 * hairlines. Read-only signals only — actions live elsewhere on the page.
 */
export function StatStrip({
  columns = 4,
  children,
}: {
  columns?: 3 | 4 | 5 | 6 | 7;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <dl className={`-ml-px -mt-px grid ${COLS[columns]}`}>{children}</dl>
    </div>
  );
}

export function StatCell({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-start gap-1.5 border-l border-t border-border/60 px-4 py-3",
        className,
      )}
    >
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="min-w-0 max-w-full truncate text-sm font-medium">{children}</dd>
    </div>
  );
}

// Callers render inside components that already subscribe via useTranslation.
export function describeExpiry(date: Date | null): { label: ReactNode; className: string } {
  if (!date) return { label: i18n.t("expiry.none"), className: "text-muted-foreground" };
  const diff = date.getTime() - Date.now();
  if (diff <= 0) return { label: i18n.t("expiry.expired"), className: "text-destructive" };
  if (diff <= 7 * DAY_MS) {
    return { label: <RelativeTime date={date} />, className: "text-amber-600 dark:text-amber-500" };
  }
  return { label: <RelativeTime date={date} />, className: "text-foreground" };
}
